import "./style.scss";

import Google from "assets/img/svg/googleIcon";
import Sage from "assets/img/svg/sageIcon";
import SettingsHeader from "components/shared/SettingsHeader";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUserListByCompany } from "store/adminPanelSlice";
import type { IIntegrations } from "store/integrationsStore";
import {
    authBexioAccount,
    authGoogleAccount,
    createIntegration,
    fetchIntegrations,
    IntegrationStatus,
} from "store/integrationsStore";
import DocoonIcon from "../../assets/img/svg/DocoonIcon";
import { useAppDispatch, useAppSelector } from "store/store";

import DiliscoLogo from "../../assets/img/Dilisco-logo.jpg";
import EBPLogo from "../../assets/img/EBP-logo.jpg";
import InterforumLogo from "../../assets/img/png/Interforum-logo.png";
import BexioIcon from "../../assets/img/svg/BexioIcon";
import AskIntegrationModal from "./AskIntegrationModal";
import DetailsDrawer from "./DetailsDrawer";
import DisconnectModal from "./DisconnectModal";
import DocoonConnectModal from "./DocoonConnectModal";
import ShowIntegrations from "./ShowIntegrations";
import type {
    IIntegrationsList,
    TDetailsDrawer,
    TDisconnectModal,
    TDocoonConnectModal,
    TIntegrationName,
} from "./types";
import { initialState } from "./types";

const IntegrationsSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings } = useCompanySettings();
    const usesInterforumEDI = getCompanySettings("uses_interforum_edi");
    const usesStatementTemplates = getCompanySettings("uses_custom_statement_templates");
    const usesAgreements = getCompanySettings("uses_agreements");
    const integrations = useAppSelector((state) => state.integrations.integrations);
    const users = useAppSelector((state) => state.adminPanel.users.company.data);
    const userMe = useAppSelector((state) => state.settings.userData);
    const [disconnectModal, setDisconnectModal] = useState<TDisconnectModal>(initialState.disconnectModal);
    const [detailsDrawer, setDetailsDrawer] = useState<TDetailsDrawer>(initialState.detailsDrawer);
    const [docoonConnectModal, setDocoonConnectModal] = useState<TDocoonConnectModal>(initialState.docoonConnectModal);
    const [askIntegrationModal, setAskIntegrationModal] = useState<{ open: boolean; integration: string }>({
        open: false,
        integration: "",
    });

    // Company-scoped partners (shared credentials): visibility must not depend on who clicked "Connecter".
    const isCompanyScoped = (integrationName: TIntegrationName) => integrationName === "docoon";

    const isConnected = (integrationName: TIntegrationName) => {
        if (integrationName === "interforum") return usesInterforumEDI;
        return !!integrations.find(
            (integration: IIntegrations) =>
                integration.partner === integrationName &&
                integration.status == IntegrationStatus.CONNECTED &&
                (isCompanyScoped(integrationName) || integration.connectedBy == userMe?.id),
        );
    };

    const getIntegrationId = (integrationName: TIntegrationName) => {
        return integrations.find(
            (integration: IIntegrations) =>
                integration.partner === integrationName &&
                (isCompanyScoped(integrationName) || integration.connectedBy == userMe?.id),
        )?.id;
    };

    const getIntegrationsByName = (integrationName: TIntegrationName) => {
        return integrations.filter(
            (integration: IIntegrations) =>
                integration.partner === integrationName && integration.status == IntegrationStatus.CONNECTED,
        );
    };

    // Find a pending active integration connected by me
    const isPendingIntegration = (integrationName: TIntegrationName) => {
        return integrations.find(
            (integration: IIntegrations) =>
                integration.partner === integrationName &&
                integration.status == IntegrationStatus.PENDING &&
                integration.connectedBy === userMe?.id,
        );
    };

    const integrationsList: IIntegrationsList = [
        {
            name: "google",
            title: "Google",
            subtitle: t("IntegrationsSettings.GoogleSubtitle"),
            icon: <Google className="google" />,
            isConnected: isConnected("google"),
            disabled: !usesStatementTemplates,
            integrations: getIntegrationsByName("google"),
            onConnect: async () => {
                // Verify if the integration ever exist with the pending status (1) before create one.
                if (!isPendingIntegration("google")) await dispatch(createIntegration({ partner: "google" }));
                localStorage.setItem("oauthRedirectionPath", "/settings/integrations");
                dispatch(authGoogleAccount());
            },
            onDisconnect: (e) => {
                e.stopPropagation();
                setDisconnectModal({
                    open: true,
                    integrationName: "Google",
                    warningText: t("IntegrationsSettings.GoogleWarning"),
                    id: getIntegrationId("google") as number,
                });
            },
            onOpenDetails: () => {
                setDetailsDrawer({
                    open: true,
                    integrationName: "Google",
                    integrations: getIntegrationsByName("google"),
                    logo: <Google className="google" />,
                });
            },
        },
        {
            name: "sage",
            title: "Sage",
            subtitle: t("IntegrationsSettings.ExtensionIsNotAvailable"),
            icon: <Sage className="sage" />,
            isConnected: isConnected("sage"),
            disabled: false,
            onConnect: () => {
                setAskIntegrationModal({ open: true, integration: "Sage" });
            },
            onDisconnect: () => {},
            onOpenDetails: () => {
                setDetailsDrawer({
                    open: true,
                    integrationName: "Sage",
                    integrations: getIntegrationsByName("sage"),
                    logo: <Sage className="sage" />,
                });
            },
        },
        {
            name: "ebp",
            title: "EBP",
            subtitle: t("IntegrationsSettings.ExtensionIsNotAvailable"),
            icon: <EBPIcon />,
            isConnected: isConnected("ebp"),
            disabled: false,
            onConnect: () => {
                setAskIntegrationModal({ open: true, integration: "EBP" });
            },
            onDisconnect: () => {},
            onOpenDetails: () => {
                setDetailsDrawer({
                    open: true,
                    integrationName: "EBP",
                    integrations: getIntegrationsByName("ebp"),
                    logo: <EBPIcon />,
                });
            },
        },
        {
            name: "dilisco",
            title: "Dilisco",
            subtitle: t("IntegrationsSettings.ExtensionIsNotAvailable"),
            icon: <DiliscoIcon />,
            isConnected: isConnected("dilisco"),
            disabled: false,
            onConnect: () => {
                setAskIntegrationModal({ open: true, integration: "Dilisco" });
            },
            onDisconnect: () => {},
            onOpenDetails: () => {
                setDetailsDrawer({
                    open: true,
                    integrationName: "Dilisco",
                    integrations: getIntegrationsByName("dilisco"),
                    logo: <DiliscoIcon />,
                });
            },
        },
        {
            name: "interforum",
            title: "Interforum",
            subtitle: isConnected("interforum")
                ? t("IntegrationsSettings.InterforumSubtitle")
                : t("IntegrationsSettings.ExtensionIsNotAvailable"),
            icon: <InterforumIcon />,
            isConnected: isConnected("interforum"),
            disabled: false,
            onConnect: () => {
                setAskIntegrationModal({ open: true, integration: "Interforum" });
            },
            onDisconnect: null,
            onOpenDetails: () => {
                setDetailsDrawer({
                    open: true,
                    integrationName: "Interforum",
                    integrations: getIntegrationsByName("interforum"),
                    logo: <InterforumIcon />,
                });
            },
        },
        {
            name: "bexio",
            title: "Bexio",
            subtitle: t("IntegrationsSettings.BexioSubtitle"),
            icon: <BexioIcon className={"logo"} />,
            isConnected: isConnected("bexio"),
            disabled: false,
            integrations: getIntegrationsByName("bexio"),
            onConnect: async () => {
                // Verify if the integration ever exist with the pending status (1) before create one.
                if (!isPendingIntegration("bexio")) {
                    await dispatch(createIntegration({ partner: "bexio" }));
                }
                localStorage.setItem("oauthRedirectionPath", "/settings/integrations");
                dispatch(authBexioAccount());
            },
            onDisconnect: () => {
                setDisconnectModal({
                    open: true,
                    integrationName: "bexio",
                    warningText: t("IntegrationsSettings.BexioWarning"),
                    id: getIntegrationId("bexio") as number,
                });
            },
            onOpenDetails: () => {
                setDetailsDrawer({
                    open: true,
                    integrationName: "Bexio",
                    integrations: getIntegrationsByName("bexio"),
                    logo: <BexioIcon />,
                });
            },
        },
        ...(usesAgreements
            ? [
                  {
                      name: "docoon",
                      title: "Docoon",
                      subtitle: t("IntegrationsSettings.DocoonSubtitle"),
                      icon: <DocoonIcon />,
                      isConnected: isConnected("docoon"),
                      disabled: false,
                      integrations: getIntegrationsByName("docoon"),
                      onConnect: () => setDocoonConnectModal({ open: true }),
                      onDisconnect: (e) => {
                          e.stopPropagation();
                          setDisconnectModal({
                              open: true,
                              integrationName: "Docoon",
                              warningText: t("IntegrationsSettings.DocoonWarning"),
                              id: getIntegrationId("docoon") as number,
                          });
                      },
                      onOpenDetails: () => {
                          setDetailsDrawer({
                              open: true,
                              integrationName: "Docoon",
                              integrations: getIntegrationsByName("docoon"),
                              logo: <DocoonIcon />,
                          });
                      },
                  },
              ]
            : []),
    ];

    useEffect(() => {
        if (!integrations.length) dispatch(fetchIntegrations());
        if (!users.length) dispatch(getUserListByCompany());
    }, [dispatch]);

    return (
        <>
            <DisconnectModal data={disconnectModal} onClose={() => setDisconnectModal(initialState.disconnectModal)} />
            <DocoonConnectModal
                data={docoonConnectModal}
                onClose={() => setDocoonConnectModal(initialState.docoonConnectModal)}
            />
            <DetailsDrawer
                data={detailsDrawer}
                onClose={() => setDetailsDrawer(initialState.detailsDrawer)}
                users={users}
            />
            <AskIntegrationModal
                integration={askIntegrationModal.integration}
                open={askIntegrationModal.open}
                onClose={() => setAskIntegrationModal({ open: false, integration: "" })}
            />
            <div className="integrations-settings">
                <SettingsHeader
                    title={t("Settings.Integrations")}
                    subtitle={t("IntegrationsSettings.IntegrationsSubtitle")}
                />
                <ShowIntegrations integrations={integrationsList} />
            </div>
        </>
    );
};

export default IntegrationsSettings;

const EBPIcon = () => {
    return <img src={EBPLogo} className="logo" />;
};
const DiliscoIcon = () => {
    return <img src={DiliscoLogo} className="logo" />;
};
const InterforumIcon = () => {
    return (
        <div>
            <img src={InterforumLogo} className="interforum-logo" />
        </div>
    );
};
